import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Phone, MessageCircle, Search, Filter, Loader2 } from 'lucide-react';
import { API_URL, authApi } from '@/lib/api';

/**
 * Live matrix of confirmed vs pending exhibitors, with one-tap WhatsApp
 * deep-links so the organising committee can chase pending ❌ ones.
 *
 * Data comes from GET /api/companies (already includes is_confirmed,
 * contact_person, unit_count, phone, whatsapp after Phase 15b migration).
 *
 * Confirmation toggle is a PATCH to /api/admin/companies/{id} (added in
 * Phase 17b — admin-guarded on the server).
 */
export default function RegistrationDashboard({ companies: initial, onUpdate }) {
  const [rows, setRows] = useState(initial || []);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');   // all | confirmed | pending
  const [saving, setSaving] = useState(null);                // company id currently saving
  const [err, setErr] = useState(null);

  React.useEffect(() => { setRows(initial || []); }, [initial]);

  const stats = useMemo(() => {
    const confirmed = rows.filter(r => r.is_confirmed).length;
    const total = rows.length;
    const pending = total - confirmed;
    const pct = total > 0 ? Math.round((confirmed / total) * 100) : 0;
    // Sum of tents / units
    const units = rows.reduce((sum, r) => sum + Number(r.unit_count || 0), 0);
    return { confirmed, pending, total, pct, units };
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter === 'confirmed' && !r.is_confirmed) return false;
      if (statusFilter === 'pending' && r.is_confirmed) return false;
      if (query.trim()) {
        const q = query.toLowerCase();
        const hay = `${r.name} ${r.contact_person || ''} ${r.phone || ''} ${r.whatsapp || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, statusFilter, query]);

  const toggleConfirm = async (row) => {
    setSaving(row.id);
    setErr(null);
    try {
      const res = await axios.patch(
        `${API_URL}/admin/companies/${row.id}`,
        { is_confirmed: !row.is_confirmed },
        { headers: { Authorization: `Bearer ${authApi.getToken()}` } }
      );
      const updated = res.data?.data;
      if (updated) {
        setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, ...updated } : r)));
        if (onUpdate) onUpdate(updated);
      }
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setSaving(null);
    }
  };

  /** Normalise a Malaysian phone number to E.164 for wa.me links. */
  const waNumber = (raw) => {
    if (!raw) return null;
    const digits = String(raw).replace(/\D/g, '');
    if (!digits) return null;
    if (digits.startsWith('60')) return digits;
    if (digits.startsWith('0')) return '6' + digits;    // 013-... → 6013...
    return '60' + digits;                                // fallback
  };

  const waLink = (row) => {
    // Prefer explicit whatsapp field; fall back to phone.
    const num = waNumber(row.whatsapp || row.phone);
    if (!num) return null;
    const msg = row.is_confirmed
      ? `Salam ${row.contact_person || row.name}, Taklimat HLN2026 lepas berkenaan penyertaan ${row.name}. Semoga persiapan lancar.`
      : `Salam ${row.contact_person || row.name}, ini pihak Urusetia HLN2026. Boleh kami sahkan penyertaan ${row.name} untuk pameran? Nak update dashboard kami.`;
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };

  const StatCard = ({ label, value, tone, testid }) => (
    <div
      className={`p-4 rounded-2xl border ${tone} flex flex-col justify-between min-h-[100px]`}
      data-testid={testid}
    >
      <div className="text-white text-3xl font-black leading-none">{value}</div>
      <div className="text-slate-300 text-xs uppercase tracking-widest font-bold mt-2">{label}</div>
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="registration-dashboard">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Confirmed ✅" value={stats.confirmed} tone="bg-emerald-500/15 border-emerald-500/40" testid="reg-stat-confirmed" />
        <StatCard label="Pending ❌"  value={stats.pending}   tone="bg-rose-500/15 border-rose-500/40"       testid="reg-stat-pending" />
        <StatCard label="Total Exhibitors" value={stats.total} tone="bg-slate-800/80 border-slate-700"       testid="reg-stat-total" />
        <StatCard label="Est. Tents / Units" value={stats.units} tone="bg-amber-500/15 border-amber-500/40"  testid="reg-stat-units" />
      </div>

      {/* Progress bar */}
      <div className="rounded-2xl border border-slate-700/60 bg-slate-900/60 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-white font-bold text-sm">Registration progress</div>
          <div className="text-emerald-400 text-lg font-black">{stats.pct}%</div>
        </div>
        <div className="w-full h-3 bg-slate-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all"
            style={{ width: `${stats.pct}%` }}
          />
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, contact, phone…"
            className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500"
            data-testid="reg-search-input"
          />
        </div>
        <div className="flex gap-1 bg-slate-900/60 border border-slate-700 rounded-lg p-1">
          {[
            { key: 'all',       label: `All (${stats.total})`,       cls: 'bg-slate-700 text-white' },
            { key: 'confirmed', label: `Confirmed (${stats.confirmed})`, cls: 'bg-emerald-500 text-white' },
            { key: 'pending',   label: `Pending (${stats.pending})`,     cls: 'bg-rose-500 text-white' },
          ].map(({ key, label, cls }) => (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              className={`px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-widest transition-all ${
                statusFilter === key ? cls : 'text-slate-400 hover:text-white'
              }`}
              data-testid={`reg-filter-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {err && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-sm">
          {err}
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-slate-700/50 bg-slate-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/80 text-slate-400 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Exhibitor</th>
                <th className="text-left px-4 py-3 font-bold">Contact</th>
                <th className="text-left px-4 py-3 font-bold">Phone / WhatsApp</th>
                <th className="text-right px-4 py-3 font-bold">Units</th>
                <th className="text-center px-4 py-3 font-bold">Status</th>
                <th className="text-right px-4 py-3 font-bold">Chase</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center p-8 text-slate-500 text-sm">
                    No exhibitors match this filter.
                  </td>
                </tr>
              )}
              {filtered.map((r) => {
                const link = waLink(r);
                return (
                  <tr
                    key={r.id}
                    className="border-t border-slate-800/60 hover:bg-slate-800/30"
                    data-testid={`reg-row-${r.id}`}
                  >
                    <td className="px-4 py-3 text-white font-semibold min-w-[180px]">{r.name}</td>
                    <td className="px-4 py-3 text-slate-300 text-xs">{r.contact_person || '—'}</td>
                    <td className="px-4 py-3 text-slate-300 text-xs whitespace-nowrap">
                      {r.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-slate-500" />{r.phone}</div>}
                      {r.whatsapp && <div className="flex items-center gap-1"><MessageCircle className="w-3 h-3 text-emerald-500" />{r.whatsapp}</div>}
                      {!r.phone && !r.whatsapp && <span className="text-slate-600">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-white font-bold">{r.unit_count ?? 1}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleConfirm(r)}
                        disabled={saving === r.id}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-black transition-all disabled:opacity-50 ${
                          r.is_confirmed
                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30'
                            : 'bg-rose-500/20 text-rose-300 border border-rose-500/40 hover:bg-rose-500/30'
                        }`}
                        title="Toggle confirmation"
                        data-testid={`reg-confirm-toggle-${r.id}`}
                      >
                        {saving === r.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : r.is_confirmed ? (
                          <CheckCircle2 className="w-3 h-3" />
                        ) : (
                          <XCircle className="w-3 h-3" />
                        )}
                        {r.is_confirmed ? 'Confirmed' : 'Pending'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {link ? (
                        <a
                          href={link}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold shadow-lg shadow-emerald-500/30"
                          data-testid={`reg-wa-link-${r.id}`}
                        >
                          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                        </a>
                      ) : (
                        <span className="text-slate-600 text-[10px] uppercase font-bold">No number</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
